import { createAsyncThunk } from '@reduxjs/toolkit';
import { resumeBlobFromResponse } from 'src/helpers/resumeFile';
import { announceAssistantMoment } from 'src/helpers/assistantMoments';
import ApiClient from 'src/helpers/apiClient';
import type { RootState } from 'src/redux/index';
import type {
  ActiveJobBodySortByEnum,
  BulkJobApplyBody,
  CreateJobApplicantMockTestBody,
  CreateMockTestBody,
  GenerateRecruiterMessageBody,
  JobApplyAssessmentBody,
  JobAssessmentSubmitBase,
  JobReportBody,
} from 'src/openapi';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import { getFormattedErr } from 'src/helpers';
import { resolvePostedSince } from 'src/constants';
import { EarnCreditGoalKey } from 'src/pages/applicantDashboard/constants';
import { invalidateEarnCreditsGoalIfClaimable } from 'src/redux/services/applicantDashboard';
import type { EarlyAccessJobBody } from 'src/pages/findJobs/earlyAccess';
import config from '../../../config';
import type { JobFilters } from '../types/candidate';

export const fetchOrgPostedJobs = createAsyncThunk(
  'org-posted/fetchJobs',
  async (orgId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetJobPostedList({
        orgId,
        isGptJob: false,
        query: '',
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

// A job list for one dashboard card. Not fetchJobs: that one fills
// state.candidate.jobList, which is Find Jobs' own list.
export const fetchDashboardJobs = createAsyncThunk(
  'jobs/fetchForDashboard',
  async (activeJobBody: EarlyAccessJobBody, { rejectWithValue, signal }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetAllActiveJobs({ activeJobBody }, { signal });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Quiet on failure: the pipeline just stays empty.
export const getAppliedJobsSummary = createAsyncThunk(
  'jobs/appliedSummary',
  async (_: void, { rejectWithValue, signal }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetAppliedJobsSummary({ signal });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const fetchJobs = createAsyncThunk(
  'jobs/fetch',
  async (
    {
      filters,
      pageNo,
      pageSize,
      applyEarlyAccess = false,
    }: {
      filters: JobFilters;
      pageSize: number;
      pageNo: number;
      // Only a list that shows the early-access row may hold postings back;
      // other screens reusing this thunk would lose them with nothing to say why.
      applyEarlyAccess?: boolean;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const activeJobBody: EarlyAccessJobBody = {
        page_size: pageSize,
        page: pageNo,
        // A token ('week') resolved to a date only now, at request time —
        // the URL carries the token so a shared link keeps meaning "this
        // week" rather than the week it was copied in.
        posted_since: resolvePostedSince(filters.postedSince),
        min_experience: filters.minExperience,
        max_experience: filters.maxExperience,
        salary_min: filters.salaryMin,
        job_type: filters.jobType,
        // Every selected mode, not just the first: work mode is
        // multi-select now, and 'All' is no longer a value.
        job_mode: filters.jobMode?.filter((mode) => mode !== 'All'),
        sort_by: filters.sortBy as ActiveJobBodySortByEnum | undefined | null,
        bookmarked: filters.bookmarked,
        salary_max: filters.salaryMax,
        company_size: filters.companySize,
        user_search_query: filters.searchQuery,
        job_role_ids: filters?.jobRoles?.map((val) => val.id ?? ''),
        skills: filters.skills?.map((val) => val.id ?? ''),
        location_ids: filters.locations
          ?.filter((val) => val.place_id && val.place_id?.trim()?.length > 0)
          .map((val) => val.place_id!),
        company_ids: filters?.companies?.map((val) => val.id ?? ''),
        company_type: filters?.companyType,
        degree_ids: filters?.degrees?.map((val) => val.id ?? ''),
        apply_early_access: applyEarlyAccess || undefined,
        early_access_only: filters.earlyAccessOnly || undefined,
      };
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetAllActiveJobs({ activeJobBody }, { signal });
      return response.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchAppliedJobs = createAsyncThunk(
  'jobs/applied',
  async (
    {
      pageNo = 1,
      pageSize = 10,
      hiringStatus,
      skillIds,
      jobStatus,
      userSearchQuery,
    }: {
      hiringStatus?: string[];
      pageNo?: number;
      pageSize?: number;
      skillIds?: string[];
      jobStatus?: string[];
      userSearchQuery?: string;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetAllAppliedJobs(
        {
          hiringStatus,
          pageSize,
          skillIds,
          userSearchQuery,
          page: pageNo,
          status: jobStatus,
        },
        { signal },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const markVoucherPopupDismissed = createAsyncThunk(
  'user/markVoucherPopupDismissed',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getUserApi()
      ).userApiMarkVoucherPopupDismissed();
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchUserSelf = createAsyncThunk(
  'user/self',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getUserApi()
      ).userApiGetUserSelf();
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getJobById = createAsyncThunk(
  'jobs/getJobById',
  async (
    { jobId, orgId }: { jobId: string; orgId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetJobById({ jobId, orgId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const applyJob = createAsyncThunk(
  'job/apply',
  async (
    {
      jobId,
      assessmentResponse,
    }: {
      jobId: string;
      assessmentResponse: JobApplyAssessmentBody;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiCreateJobApplicants({
        jobId,
        jobApplyAssessmentBody: assessmentResponse,
      });
      // Turns the "apply to your first job" goal green without a reload. Done here
      // rather than at the four call sites so every apply surface is covered, and it
      // no-ops once that goal is complete.
      dispatch(
        invalidateEarnCreditsGoalIfClaimable(
          EarnCreditGoalKey.FirstJobApplication,
        ),
      );
      announceAssistantMoment('job_applied', jobId);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const submitJobAssessment = createAsyncThunk(
  'job/submit-assessment',
  async (
    {
      jobId,
      assessmentResponse,
    }: {
      jobId: string;
      assessmentResponse: JobAssessmentSubmitBase;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiSubmitJobAssessment({
        jobId,
        jobAssessmentSubmitBase: assessmentResponse,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updatePreference = createAsyncThunk(
  'job/candidate-meta-details',
  async ({ obj }: { obj: any }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiUpsertCandidateMetaDetails(obj);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const jobByIdOrAliasForCandidate = createAsyncThunk(
  'fetch/detail-job',
  async (
    {
      idOrAlias,
      // Set while another identifier is still to be tried, so a first miss
      // doesn't put an error in front of the candidate before we've given up.
      quiet,
    }: { idOrAlias: string; quiet?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetJobByIdSlim({ jobId: idOrAlias });
      return response.data;
    } catch (err: any) {
      if (!quiet) {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const jobByIdForApplied = createAsyncThunk(
  'fetch/applied-job-detail',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetJobByIdForApplied({ jobId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchJobIdOrgId = createAsyncThunk(
  'jobs/jobId-orgId',
  async ({ jobAlias }: { jobAlias: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetOrgJobIds({ jobAlias });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchEmailPreferences = createAsyncThunk(
  'jobs/email-preferences',
  async ({ type }: { type: any }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getNotificationsApi()
      ).notificationApiGetUserNotificationsByUserType(type);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const emailNotificationsToggle = createAsyncThunk(
  'jobs/email-notification-toggle',
  async ({ obj }: { obj: any }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getNotificationsApi()
      ).notificationApiToggleEmailNotifications(obj);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getSkillTestById = createAsyncThunk(
  'get/skill-test-by-id',
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getMockTestApi()
      ).mocktestApiGetMocktestById({ mocktestId: id });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const buySkillTest = createAsyncThunk(
  'jobs/buy-skill-test',
  async (
    { obj }: { obj: CreateJobApplicantMockTestBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getMockTestApi()
      ).mocktestApiCreateMocktestJobs({ createJobApplicantMockTestBody: obj });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const buyRecommendedSkillTest = createAsyncThunk(
  'jobs/buy-recommended-skill-test',
  async ({ obj }: { obj: CreateMockTestBody }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getMockTestApi()
      ).mocktestApiCreateMocktest({ createMockTestBody: obj });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getRecommendedSkillTest = createAsyncThunk(
  'jobs/get-recommended-skill-test',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetJobRelatedMocktests({ jobId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getSimilarJobs = createAsyncThunk(
  'get/similar-jobs',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetSimilarJobs({ jobId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAnsweredQuestions = createAsyncThunk(
  'get/attempted-questions',
  async (
    {
      competitionId,
      examId,
      filterType,
      pageNo,
      pageSize,
      searchString,
      skillIds,
    }: {
      competitionId?: string;
      examId?: string;
      filterType?: string;
      pageNo?: number;
      pageSize?: number;
      searchString?: string;
      skillIds?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getQuestionApi()
      ).questionApiGetAnsweredQuestionList({
        competitionId,
        examId,
        filterType,
        pageSize,
        searchString,
        skillIds,
        page: pageNo,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAppliedJobResume = createAsyncThunk(
  'get/applied-job-resume',
  async (
    { jobId }: { jobId: string },
    { rejectWithValue, signal, getState, fulfillWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const res = await axios.get(
        `${config.API_URL}/job/${jobId}/applicant-resume`,
        {
          signal,
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${state.auth.token}`,
          },
        },
      );

      return fulfillWithValue({ data: resumeBlobFromResponse(res) });
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const getApplicantStatusInfo = createAsyncThunk(
  'get/applicant-feature-status-info',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getUserApi()
      ).userApiGetApplicantStatusInfo();
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const markJobAsNotInterested = createAsyncThunk(
  'jobs/not-interested',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiMarkJobAsNotInterested({ jobId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const startJobPreAnalysis = createAsyncThunk(
  'jobs/start-job-pre-analysis',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiInitiateJobApplicationPreAnalysis({ jobId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const fetchMyFeedbacks = createAsyncThunk(
  'support/my-feedbacks/list',
  async (
    {
      pageNo,
      pageSize,
      status,
    }: { pageNo?: number; pageSize?: number; status?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getGeneralApi()
      ).generalApiListMyFeedbacks({ pageNo, pageSize, status });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchMyFeedbackThread = createAsyncThunk(
  'support/my-feedback/thread',
  async ({ feedbackId }: { feedbackId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getGeneralApi()
      ).generalApiGetMyFeedbackThread({ feedbackId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const replyToMyFeedback = createAsyncThunk(
  'support/my-feedback/reply',
  async (
    {
      feedbackId,
      body,
      mediaUrlList,
    }: { feedbackId: string; body: string; mediaUrlList?: string[] },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getGeneralApi()
      ).generalApiReplyToMyFeedback({
        feedbackId,
        feedbackReplyBody: {
          body,
          media_url_list: mediaUrlList,
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const getApplicantDashboardData = createAsyncThunk(
  'dashboard/get/applicant-dashboard-data',
  async ({ skillIds }: { skillIds?: string[] }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getDashboardApi()
      ).dashboardApiGetApplicantDashboardData({ skillIds });
      return response.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

// Quiet on failure: the claim strip is a nudge, and the page renders without it.
export const getPendingCertificateClaims = createAsyncThunk(
  'dashboard/get/pending-certificate-claims',
  async ({ signal }: { signal?: AbortSignal }, { rejectWithValue }) => {
    try {
      const data = await ApiClient.getPendingCertificateClaims(signal);
      return data.claims ?? [];
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getActivePopupBannerForPage = createAsyncThunk(
  'dashboard/get/active-popup-banner',
  async ({ page }: { page: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getDashboardApi()
      ).dashboardApiGetActivePopupBanner({ page });
      return response.data.banner ?? null;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getCommunityActivity = createAsyncThunk(
  'get/community-activity',
  async (
    {
      pageNo,
      pageSize,
      skillId,
    }: {
      pageNo?: number | undefined;
      pageSize?: number | undefined;
      skillId?: string;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const res = await (
        await ApiClient.getUserApi()
      ).userApiGetUserCommunityUpdatesActivity(
        {
          page: pageNo,
          pageSize,
          skillId,
        },
        { signal },
      );
      return res.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const getProfileMini = createAsyncThunk(
  'get/<username>/profile/mini',
  async ({ username }: { username: string }, { rejectWithValue, signal }) => {
    try {
      const res = await (
        await ApiClient.getUserApi()
      ).userApiGetMiniProfile(
        { username },
        {
          signal,
        },
      );
      return res.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

/**
 * Learners holding a live socket right now, newest presence state each call.
 *
 * Platform-wide on purpose — presence belongs to the person, not to whichever
 * room is asking, so this is callable before a room exists. There is no
 * presence broadcast to subscribe to, so callers poll.
 */
export const getOnlineUsers = createAsyncThunk(
  'user/online-users',
  async (
    {
      skillIds,
      roomId,
      page = 1,
      pageSize = 10,
      forInvite,
      excludeInvited,
    }: {
      skillIds?: string[];
      page?: number;
      pageSize?: number;
      roomId?: string;
      /** Order for asking someone to play, not for browsing who is best. */
      forInvite?: boolean;
      /** Skip anyone still holding a live invite to this room. */
      excludeInvited?: boolean;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const res = await (
        await ApiClient.getUserApi()
      ).userApiGetOnlineUsers(
        { skillIds, page, pageSize, roomId, forInvite, excludeInvited },
        { signal },
      );
      return res.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const toggleBookmarkJob = createAsyncThunk(
  'job/bookmark',
  async (
    { jobId, bookmark }: { jobId: string; bookmark: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiUpdateJobBookmark({ jobId, bookmark });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
export const reportJob = createAsyncThunk(
  'post/report-job',
  async ({ body }: { body: JobReportBody }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiCreateJobReport({ jobReportBody: body });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const enableAutoJobApply = createAsyncThunk(
  'job/enable-auto-job-apply',
  async (_, { rejectWithValue }) => {
    try {
      const res = await (
        await ApiClient.getJobApi()
      ).jobApiEnableAutoJobApply();
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const disableAutoJobApply = createAsyncThunk(
  'job/disable-auto-job-apply',
  async (_, { rejectWithValue }) => {
    try {
      const res = await (
        await ApiClient.getJobApi()
      ).jobApiDisableAutoJobApply();
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const bulkJobApply = createAsyncThunk(
  'job/bulk-job-apply',
  async (data: BulkJobApplyBody, { rejectWithValue }) => {
    try {
      const res = await (
        await ApiClient.getJobApi()
      ).jobApiCreateBulkJobsApplicants({ bulkJobApplyBody: data });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const getResumeReport = createAsyncThunk(
  'get/resume-research-report',
  async (_, { rejectWithValue }) => {
    try {
      const res = await (
        await ApiClient.getFilesApi()
      ).filesApiGetResumeResearchReport();
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const autoApplyJobEnable = createAsyncThunk(
  'job/auto-apply-enable',
  async (_, { rejectWithValue }) => {
    try {
      const res = await (
        await ApiClient.getJobApi()
      ).jobApiEnableAutoJobApply();
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const markAutoApplyDialogSeen = createAsyncThunk(
  'post/autoapply-dialog-seen',
  async (_, { rejectWithValue }) => {
    try {
      const res = await (
        await ApiClient.getUserApi()
      ).userApiMarkAutoApplyDialogShown();
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getCompanyJobList = createAsyncThunk(
  'get/job/<company-alias>/jobs/list',
  async (
    {
      companyAlias,
      page,
      pageSize,
    }: {
      companyAlias: string;
      page?: number;
      pageSize?: number;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const res = await (
        await ApiClient.getJobApi()
      ).jobApiGetPublicCompanyJobs(
        { companyAlias, page, pageSize },
        {
          signal,
        },
      );
      return res.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const getCompanyJobStatistics = createAsyncThunk(
  'get/job/<company-alias>/statistics',
  async (
    {
      companyAlias,
    }: {
      companyAlias: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await ApiClient.getJobApi()
      ).jobApiGetPublicCompanyStatistics({ companyAlias });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const withdrawJobApplication = createAsyncThunk(
  'put/withdraw-job-application',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiWithdrawJobApplication({ jobId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const handleGenerateSpotlightMessage = createAsyncThunk(
  'post/chat/generate-message',
  async (
    {
      jobId,
      body,
    }: {
      jobId: string;
      body: GenerateRecruiterMessageBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGenerateRecruiterMessage({
        jobId,
        generateRecruiterMessageBody: body,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const revealRecruitersEmail = createAsyncThunk(
  'post/chat/generate-message',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetRecruiterEmail({ jobId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
